import { Gear, Moon, Sun, SunHorizon } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { getLocale } from "@plannello/i18n/runtime";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Form } from "react-aria-components";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { Avatar, Button, FormSection, InlineError, RowSkeleton, Select, Switch, TextField } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { shortcutsSheetFlag } from "@/lib/commands";
import { LOCALES } from "@/lib/locale";
import { type Density, densityPref, singleKeyShortcutsPref, type Theme, themePref, usePref } from "@/lib/prefs";
import { type Locale, type Me, meQuery, useMe } from "@/lib/queries";
import { useChangeLocale } from "@/lib/session";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/settings/")({ component: Settings });

function Settings() {
  useBreadcrumbs([{ label: m.nav_settings() }]);
  const meQ = useMe();

  return (
    <Page title={m.nav_settings()} icon={Gear} meta={m.settings_meta()} width="full" className="max-w-5xl">
      {meQ.isPending ? (
        <RowSkeleton rows={5} />
      ) : meQ.isError ? (
        <InlineError message={m.error_generic()} onRetry={() => void meQ.refetch()} isRetrying={meQ.isFetching} />
      ) : (
        <SettingsForm me={meQ.data} />
      )}
    </Page>
  );
}

function SettingsForm({ me }: { me: Me }) {
  const [theme, setTheme] = usePref(themePref);
  const [density, setDensity] = usePref(densityPref);
  const [singleKey, setSingleKey] = usePref(singleKeyShortcutsPref);
  const changeLocale = useChangeLocale();

  return (
    <div className="flex flex-col">
      <FormSection title={m.settings_profile()} description={m.settings_profile_hint()}>
        <div className="flex items-center gap-3">
          <Avatar name={me.name} seed={me.id} src={me.image} bot={me.kind === "bot"} size="xl" labelled={false} />
          <div className="flex min-w-0 flex-col">
            <span className="truncate-title text-sm font-medium text-fg" title={me.name}>
              {me.name}
            </span>
            <span className="truncate-title text-sm text-fg-muted" title={me.email}>
              {me.email}
            </span>
          </div>
        </div>
        <NameForm me={me} />
      </FormSection>

      <FormSection title={m.settings_language()} description={m.settings_language_hint()}>
        <Select
          id="settings-language"
          label={m.settings_language()}
          labelHidden
          items={LOCALES.map((l) => ({ id: l.value, label: l.label }))}
          selectedKey={getLocale()}
          onSelectionChange={(k) => changeLocale.mutate({ locale: k as Locale, refocusId: "settings-language" })}
        />
        {changeLocale.isError ? (
          <InlineError
            message={m.settings_language_error()}
            onRetry={() => changeLocale.mutate({ ...changeLocale.variables })}
          />
        ) : null}
      </FormSection>

      <FormSection title={m.settings_appearance()} description={m.settings_appearance_hint()}>
        <Select
          label={m.settings_theme()}
          items={[
            { id: "system", label: m.theme_system(), icon: SunHorizon },
            { id: "light", label: m.theme_light(), icon: Sun },
            { id: "dark", label: m.theme_dark(), icon: Moon },
          ]}
          selectedKey={theme}
          onSelectionChange={(k) => setTheme(k as Theme)}
        />
        <Select
          label={m.settings_density()}
          description={m.settings_density_hint()}
          items={[
            { id: "compact", label: m.density_compact() },
            { id: "comfortable", label: m.density_comfortable() },
          ]}
          selectedKey={density}
          onSelectionChange={(k) => setDensity(k as Density)}
        />
      </FormSection>

      <FormSection title={m.settings_keyboard()} description={m.settings_keyboard_hint()}>
        <Switch isSelected={singleKey} onChange={setSingleKey} description={m.settings_single_key_hint()}>
          {m.settings_single_key()}
        </Switch>
        <div>
          <Button variant="secondary" onPress={() => shortcutsSheetFlag.set(true)}>
            {m.palette_shortcuts()}
          </Button>
        </div>
      </FormSection>
    </div>
  );
}

function NameForm({ me }: { me: Me }) {
  const qc = useQueryClient();
  const [name, setName] = useState(me.name);
  const [error, setError] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (next: string) => api.patch("/api/v1/me", { name: next }),
    onSuccess: (_d, next) => {
      qc.setQueryData<Me>(meQuery.queryKey, (old) => (old ? { ...old, name: next } : old));
      toast.show({ title: m.settings_saved(), tone: "success" });
    },
    onError: (e) => setError(e instanceof ApiError && e.fields?.name ? e.fields.name : m.error_generic()),
  });
  const dirty = name.trim() !== me.name && name.trim().length > 0;

  return (
    <Form
      validationBehavior="aria"
      onSubmit={(e) => {
        e.preventDefault();
        setError(undefined);
        if (dirty) save.mutate(name.trim());
      }}
      className="flex flex-col gap-3"
    >
      <TextField label={m.auth_name()} name="name" autoComplete="name" value={name} onChange={setName} error={error} />
      {/* Actions appear only once there is something to save. */}
      {dirty || save.isPending ? (
        <div className="anim-fade flex items-center gap-2">
          <Button type="submit" variant="primary" isPending={save.isPending}>
            {m.common_save()}
          </Button>
          <Button
            variant="ghost"
            onPress={() => {
              setName(me.name);
              setError(undefined);
            }}
          >
            {m.common_cancel()}
          </Button>
        </div>
      ) : null}
    </Form>
  );
}
